import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Google Places hook using the Maps JavaScript API (client-side, CORS-safe).
 * Uses legacy AutocompleteService + PlacesService with a pinned SDK version
 * to avoid breaking changes in the latest Google Maps SDK.
 */

export function useGooglePlaces(sdkLoaded = false) {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placeDetails, setPlaceDetails] = useState(null);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const dummyDiv = useRef(null);

  // The parent owns script loading through useLoadScript. Initialize services
  // only after that loader reports that Places is available.
  useEffect(() => {
    if (!sdkLoaded || !window.google?.maps?.places) return;

    try {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      if (!dummyDiv.current) {
        dummyDiv.current = document.createElement('div');
      }
      placesService.current = new window.google.maps.places.PlacesService(
        dummyDiv.current
      );
    } catch (err) {
      console.error('[GooglePlaces] Service init error:', err);
    }
  }, [sdkLoaded]);

  const searchPlaces = useCallback(
    async (input, requestOptions = {}) => {
      if (!input || input.length < 3) {
        setPredictions([]);
        return;
      }

      // Wait for SDK if not ready
      if (!autocompleteService.current) {
        if (!sdkLoaded || !window.google?.maps?.places) {
          setPredictions([]);
          return;
        }

        try {
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
          if (!dummyDiv.current) {
            dummyDiv.current = document.createElement('div');
          }
          placesService.current = new window.google.maps.places.PlacesService(
            dummyDiv.current
          );
        } catch {
          console.error('Cannot search — Google Places service is unavailable');
          return;
        }
      }

      setLoading(true);
      try {
        autocompleteService.current.getPlacePredictions(
          {
            input,
            componentRestrictions: { country: 'us' },
            ...requestOptions
          },
          (results, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              results &&
              results.length > 0
            ) {
              setLoading(false);
              setPredictions(
                results.map((r) => ({
                  place_id: r.place_id,
                  description: r.description,
                  structured_formatting: {
                    main_text:
                      r.structured_formatting?.main_text || r.description,
                    secondary_text:
                      r.structured_formatting?.secondary_text || ''
                  }
                }))
              );
            } else if (placesService.current) {
              // Fallback to textSearch if Autocomplete returned ZERO_RESULTS
              placesService.current.textSearch(
                { query: input },
                (textResults, textStatus) => {
                  setLoading(false);
                  if (
                    textStatus ===
                      window.google.maps.places.PlacesServiceStatus.OK &&
                    textResults &&
                    textResults.length > 0
                  ) {
                    setPredictions(
                      textResults.slice(0, 5).map((r) => ({
                        place_id: r.place_id,
                        description: `${r.name}, ${r.formatted_address || ''}`,
                        structured_formatting: {
                          main_text: r.name || input,
                          secondary_text: r.formatted_address || ''
                        }
                      }))
                    );
                  } else {
                    setPredictions([]);
                  }
                }
              );
            } else {
              setLoading(false);
              setPredictions([]);
            }
          }
        );
      } catch (error) {
        console.error('Error fetching place predictions:', error);
        setPredictions([]);
        setLoading(false);
      }
    },
    [sdkLoaded]
  );

  const getPlaceDetails = useCallback(
    async (placeId) => {
      // Wait for SDK if not ready
      if (!placesService.current) {
        if (!sdkLoaded || !window.google?.maps?.places) return;

        try {
          if (!dummyDiv.current) {
            dummyDiv.current = document.createElement('div');
          }
          placesService.current = new window.google.maps.places.PlacesService(
            dummyDiv.current
          );
        } catch {
          console.error('Cannot get details — Google Maps SDK failed to load');
          return;
        }
      }

      setLoading(true);
      try {
        placesService.current.getDetails(
          {
            placeId,
            fields: ['name', 'formatted_address', 'geometry', 'place_id']
          },
          (place, status) => {
            setLoading(false);
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place
            ) {
              setPlaceDetails({
                place_id: placeId,
                name: place.name,
                formatted_address: place.formatted_address,
                geometry: {
                  location: {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                  }
                }
              });
            } else {
              console.error('Place details failed with status:', status);
              setPlaceDetails(null);
            }
          }
        );
      } catch (error) {
        console.error('Error fetching place details:', error);
        setPlaceDetails(null);
        setLoading(false);
      }
    },
    [sdkLoaded]
  );

  const clearPredictions = useCallback(() => setPredictions([]), []);

  return {
    predictions,
    loading,
    placeDetails,
    searchPlaces,
    getPlaceDetails,
    clearPredictions
  };
}
